import { useAuth0 } from '@auth0/auth0-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useInvalidateQueries, useTableData } from '../../utils/apiHooks';
import { useDataReload } from '../renderer/DataReloadContext';
import ColumnVisibilityDropdown from './table/subComponents/ColumnVisibilityDropdown';
import TableBody from './table/TableBody';
import TableHeader from './table/TableHeader';
import TablePagination from './table/TablePagination';
import { Column, EptDataTableProps } from './table/types';
import { buildQueryParams } from './table/utils';

import './table/styles.css';

const EptDataTable: React.FC<EptDataTableProps> = ({ block }) => {
  const { user, getIdTokenClaims } = useAuth0();
  const { registerReloadFunction, unregisterReloadFunction } = useDataReload();
  const navigate = useNavigate();
  const location = useLocation();

  // Get initial state from URL parameters
  const urlParams = new URLSearchParams(location.search);
  const initialPage =
    parseInt(urlParams.get('page')) || block.default_query_params?.page || 1;
  const initialFilters = {};
  const initialSearch = urlParams.get(block.search?.query_param) || '';
  const [search, setSearch] = useState(initialSearch);

  // Parse filters from URL
  urlParams.forEach((value, key) => {
    if (
      key !== 'page' &&
      key !== 'resultsPerPage' &&
      key !== block.search?.query_param
    ) {
      try {
        const parsed = JSON.parse(value);
        // If the parsed value is an array, keep it as a JSON string
        // to match how dropdown filters work (which use string format "[...]")
        // This prevents axios from auto-serializing arrays as key[]=value
        if (Array.isArray(parsed)) {
          initialFilters[key] = value; // Keep as JSON string
        } else {
          initialFilters[key] = parsed;
        }
      } catch {
        initialFilters[key] = value;
      }
    }
  });

  // React Query will manage data, loading, and error states
  const [currentParams, setCurrentParams] = useState<Record<string, any>>({
    ...block.default_query_params,
    page: initialPage,
    ...(initialSearch &&
      block.search?.query_param && {
        [block.search.query_param]: initialSearch,
      }),
  });
  const [selectedFilters, setSelectedFilters] =
    useState<Record<string, any>>(initialFilters);
  const [selectedSort, setSelectedSort] = useState<Record<string, any>>({});
  const [showColumnVisibility, setShowColumnVisibility] =
    useState<boolean>(false);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    block.columns.map(col => col.key)
  );
  const [showScrollLeft, setShowScrollLeft] = useState<boolean>(false);
  const [showScrollRight, setShowScrollRight] = useState<boolean>(false);
  const [currentDataApi, setCurrentDataApi] = useState<string>(block.data_api);
  const [columnWidths, setColumnWidths] = useState<Record<string, string>>(
    () => {
      // Initialize column widths from block.columns or set defaults
      const initialWidths = {};
      block.columns.forEach(column => {
        initialWidths[column.key] = column.width || '150px';
      });
      return initialWidths;
    }
  );
  const [columnMinWidths, setColumnMinWidths] = useState<
    Record<string, string>
  >(() => {
    // Initialize min-widths from block.columns or set defaults
    const initialMinWidths = {};
    block.columns.forEach(column => {
      initialMinWidths[column.key] = column.width || '150px';
    });
    return initialMinWidths;
  });
  const tableRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Function to analyze content and determine optimal column width for specific column types only
  const analyzeColumnWidth = useCallback(
    (column: Column, data: any[]): string => {
      if (!data || data.length === 0) {
        return column.width || '150px'; // Keep original default
      }

      // Only analyze specific column types that need optimization
      const isNumeric =
        column.type_ === 'numeric' ||
        column.type_ === 'number' ||
        ['count', 'uses', 'ks_id', 'ksid', 'ks id'].some(pattern =>
          column.key.toLowerCase().includes(pattern.toLowerCase())
        );

      const isFileType =
        column.key.toLowerCase() === 'type' ||
        column.key.toLowerCase().includes('type');

      const isTags = column.key === 'tags' || column.key === 'status';
      const isAction =
        column.type_ === 'icon' || column.type_ === 'complex_icon';
      const isDate =
        column.type_ === 'date' ||
        ['updated', 'created', 'date', 'time'].some(pattern =>
          column.key.toLowerCase().includes(pattern)
        );
      const isBoolean =
        column.type_ === 'boolean' ||
        ['confidential', 'active', 'enabled', 'visible'].some(pattern =>
          column.key.toLowerCase().includes(pattern)
        );
      const isUrl =
        column.type_ === 'link' ||
        ['url', 'link'].some(pattern =>
          column.key.toLowerCase().includes(pattern)
        );

      // Check if this is a KS ID column specifically (should be wider for "ks id" text)
      const isKsId = ['ks_id', 'ksid', 'ks id'].some(pattern =>
        column.key.toLowerCase().includes(pattern.toLowerCase())
      );

      // For numeric columns (count/uses) - make them very compact
      if (isNumeric) {
        // KS ID columns need more width to fit "ks id" text on one line
        if (isKsId) {
          return '80px'; // Wider for "ks id" text
        }
        return '60px'; // Very compact for numeric values
      }

      // For file type columns - make them compact
      if (isFileType) {
        return '80px'; // Slightly wider for file extensions
      }

      // For tags columns - analyze content to reduce white space
      if (isTags) {
        const sampleData = data.slice(0, 5); // Small sample
        let maxTagLength = 0;

        sampleData.forEach(row => {
          const value = row[column.key];
          if (Array.isArray(value)) {
            // Calculate based on number of tags and their lengths
            const totalLength = value.reduce((acc, tag) => {
              const tagStr =
                typeof tag === 'string'
                  ? tag
                  : tag.tag_value || tag.tag_type || '';
              return acc + tagStr.length;
            }, 0);
            maxTagLength = Math.max(maxTagLength, totalLength);
          } else if (typeof value === 'string') {
            maxTagLength = Math.max(maxTagLength, value.length);
          }
        });

        // Return compact width based on content
        if (maxTagLength < 20) return '100px';
        if (maxTagLength < 40) return '150px';
        return '200px'; // Still compact but allows for more tags
      }

      // For action columns - analyze content to determine optimal width
      if (isAction) {
        const sampleData = data.slice(0, 5); // Small sample
        let maxButtonCount = 0;

        sampleData.forEach(row => {
          const value = row[column.key];
          if (Array.isArray(value)) {
            maxButtonCount = Math.max(maxButtonCount, value.length);
          } else if (value && typeof value === 'object') {
            // Count sub_keys or subKeys
            const subKeys = value.sub_keys || value.subKeys || [];
            maxButtonCount = Math.max(maxButtonCount, subKeys.length);
          }
        });

        // Return appropriate width based on number of buttons
        // For two buttons: ~250px, for one button: adjust accordingly
        if (maxButtonCount <= 1) return '120px'; // Single button with padding
        if (maxButtonCount === 2) return '250px'; // Two buttons
        if (maxButtonCount <= 3) return '300px'; // Three buttons
        return '350px'; // More buttons - wider
      }

      // For date columns - make them compact for dates like "Oct 3, 25 8:58 PM"
      if (isDate) {
        return '120px'; // Compact width for date columns
      }

      // For boolean/checkbox columns - make them very compact for checkboxes
      if (isBoolean) {
        return '40px'; // Very compact for checkboxes
      }

      // For URL columns - make them same width as text columns
      if (isUrl) {
        return '200px'; // Same width as text columns
      }

      // For all other columns, return the original width
      return column.width || '150px';
    },
    []
  );

  // Build query parameters for API call
  const queryParams = buildQueryParams(
    currentParams,
    selectedFilters,
    selectedSort
  );

  // Use React Query for data fetching
  const {
    data: queryResult,
    isLoading: loading,
    error,
    refetch: refetchData,
  } = useTableData(currentDataApi, queryParams, {
    enabled: !!user, // Only fetch when user is authenticated
  });

  // Extract data and total from query result
  const data = queryResult?.data || [];
  const totalRows = queryResult?.total || 0;

  // Store ownership data in sessionStorage when deal_room_list data arrives
  useEffect(() => {
    if (
      currentDataApi?.includes('/deal_room_list') &&
      data &&
      data.length > 0
    ) {
      try {
        const ownership: Record<string, boolean> = {};
        data.forEach((row: any) => {
          const id = row?.id;
          const user_role = (row?.user_role || '').toString().toLowerCase();
          if (id) {
            ownership[id] = user_role === 'owner';
          }
        });
        sessionStorage.setItem('dealroom_ownership', JSON.stringify(ownership));
        console.log(
          '[EptDataTable] Stored ownership for',
          Object.keys(ownership).length,
          'deal rooms'
        );
      } catch (e) {
        console.error('[EptDataTable] Failed to set sessionStorage:', e);
      }
    }
  }, [data, currentDataApi]);

  // Auto-adjust column widths only for specific column types that need optimization
  // Only run once when data first loads, then allow manual resizing
  useEffect(() => {
    if (data && data.length > 0 && block.columns) {
      const newWidths: Record<string, string> = { ...columnWidths };
      let hasChanges = false;

      block.columns.forEach(column => {
        const currentWidth = columnWidths[column.key];

        // Skip if width already set (user may have resized)
        if (
          currentWidth &&
          currentWidth !== '150px' &&
          currentWidth !== column.width
        ) {
          return; // Preserve user-set width
        }

        // Only optimize specific column types on initial load
        const isNumeric =
          column.type_ === 'numeric' ||
          column.type_ === 'number' ||
          ['count', 'uses', 'ks_id', 'ksid', 'ks id'].some(pattern =>
            column.key.toLowerCase().includes(pattern.toLowerCase())
          );
        const isFileType =
          column.key.toLowerCase() === 'type' ||
          column.key.toLowerCase().includes('type');
        const isTags = column.key === 'tags' || column.key === 'status';
        const isAction =
          column.type_ === 'icon' || column.type_ === 'complex_icon';
        const isDate =
          column.type_ === 'date' ||
          ['updated', 'created', 'date', 'time'].some(pattern =>
            column.key.toLowerCase().includes(pattern)
          );
        const isBoolean =
          column.type_ === 'boolean' ||
          ['confidential', 'active', 'enabled', 'visible'].some(pattern =>
            column.key.toLowerCase().includes(pattern)
          );
        const isUrl =
          column.type_ === 'link' ||
          ['url', 'link'].some(pattern =>
            column.key.toLowerCase().includes(pattern)
          );

        // Check if this is a feedback/compound column
        const isFeedback =
          column.type_ === 'compound' && column.key === 'feedback';

        // Set min-width for all columns when first analyzing
        // This ensures columns don't shrink below their initial/optimal width
        if (
          isNumeric ||
          isFileType ||
          isTags ||
          isAction ||
          isDate ||
          isBoolean ||
          isUrl ||
          isFeedback
        ) {
          const optimalWidth = analyzeColumnWidth(column, data);
          const currentWidthNum = parseInt(
            currentWidth?.replace('px', '') || '150'
          );
          const optimalWidthNum = parseInt(optimalWidth.replace('px', ''));

          // Only update if there's a significant difference and width hasn't been manually set
          if (Math.abs(currentWidthNum - optimalWidthNum) > 10) {
            newWidths[column.key] = optimalWidth;
            hasChanges = true;
          }

          // Set min-width for these column types (once)
          // For numeric columns, use 80px min-width instead of optimal width
          // For feedback columns, use 150px min-width
          if (
            !columnMinWidths[column.key] ||
            columnMinWidths[column.key] === '150px'
          ) {
            let minWidthValue = optimalWidth;
            if (isNumeric) {
              // Check if this is a KS ID column (should be wider)
              const isKsId = ['ks_id', 'ksid', 'ks id'].some(pattern =>
                column.key.toLowerCase().includes(pattern.toLowerCase())
              );
              minWidthValue = isKsId ? '90px' : '80px';
            } else if (isFeedback) {
              minWidthValue = '150px';
            }

            setColumnMinWidths(prev => ({
              ...prev,
              [column.key]: minWidthValue,
            }));
          }
        } else {
          // For text columns and others, ensure min-width is set to current width (once)
          if (
            !columnMinWidths[column.key] ||
            columnMinWidths[column.key] === '150px'
          ) {
            setColumnMinWidths(prev => ({
              ...prev,
              [column.key]: currentWidth || column.width || '150px',
            }));
          }
        }
      });

      if (hasChanges) {
        setColumnWidths(newWidths);
      }
    }
  }, [data, block.columns, analyzeColumnWidth]); // Removed columnWidths from deps to prevent re-running on resize

  // Get invalidation utilities
  const { invalidateTableData } = useInvalidateQueries();

  // Function to extract response_id from the data API URL
  const extractResponseId = (apiUrl: string): string | null => {
    if (!apiUrl) return null;

    try {
      const url = new URL(apiUrl);
      return url.searchParams.get('response_id');
    } catch (error) {
      console.warn('Error parsing API URL:', error);
      return null;
    }
  };

  // Get response_id from current data API
  const responseId = extractResponseId(currentDataApi);

  // Add toggle button event listener
  useEffect(() => {
    const handleToggleButtonStateChange = event => {
      const { newApiEndpoint } = event.detail;
      if (newApiEndpoint) {
        setCurrentDataApi(newApiEndpoint);
        // Reset to first page when switching between states
        setCurrentParams(prev => ({ ...prev, page: 1 }));
        // Invalidate queries for the old endpoint
        invalidateTableData();
      }
    };

    const tableElement = tableRef.current;
    if (tableElement) {
      tableElement.addEventListener(
        'toggleButtonStateChange',
        handleToggleButtonStateChange
      );

      return () => {
        tableElement.removeEventListener(
          'toggleButtonStateChange',
          handleToggleButtonStateChange
        );
      };
    }
  }, [invalidateTableData]);

  // Add debounced search handler using useCallback
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (searchTerm: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          setCurrentParams(prev => {
            const newParams = { ...prev, page: 1 };

            if (
              searchTerm &&
              searchTerm.trim() !== '' &&
              block.search?.query_param
            ) {
              // Add search parameter if there's a search term
              newParams[block.search.query_param] = searchTerm;
            } else if (block.search?.query_param) {
              // Remove search parameter if search is empty
              delete newParams[block.search.query_param];
            }

            return newParams;
          });
        }, 500); // Wait 500ms after user stops typing
      };
    })(),
    [block.search?.query_param]
  );

  // Update URL when filters or pagination change
  const updateUrl = (
    params: Record<string, any>,
    filters: Record<string, any>
  ) => {
    console.log('updateUrl called with params:', params, 'filters:', filters);
    const urlParams = new URLSearchParams();

    // Add pagination params
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        console.log('Adding param:', key, '=', value);
        urlParams.set(key, value);
      }
    });

    // Add filter params
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        console.log('Adding filter:', key, '=', value);
        urlParams.set(
          key,
          typeof value === 'object' ? JSON.stringify(value) : value
        );
      }
    });

    console.log('Final URL params:', urlParams.toString());

    // Update URL without reloading the page
    navigate(
      {
        pathname: location.pathname,
        search: urlParams.toString(),
      },
      { replace: true }
    );
  };

  // Create a stable refetch function for compatibility with existing code
  const fetchData = useCallback(() => {
    console.log('Filter Debug - Current Params:', currentParams);
    console.log('Filter Debug - Selected Filters:', selectedFilters);
    console.log('Filter Debug - Final API Params:', queryParams);
    console.log('Filter Debug - API URL:', currentDataApi);

    // Refetch data using React Query
    refetchData();
  }, [refetchData]); // Only depend on refetchData which is stable from React Query

  // Register reload function with context (after fetchData is defined)
  useEffect(() => {
    const tableId =
      block.id || `table-${Math.random().toString(36).substr(2, 9)}`;
    registerReloadFunction(tableId, fetchData);

    return () => {
      unregisterReloadFunction(tableId);
    };
  }, [fetchData, registerReloadFunction, unregisterReloadFunction, block.id]);

  // Add this new state to track if it's the initial load
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);

  // Handle URL updates when parameters change
  useEffect(() => {
    if (!user) return;

    // Only update URL if it's not the initial load
    if (!isInitialLoad) {
      updateUrl(currentParams, selectedFilters);
    }

    // Mark as not initial load after first render
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [currentParams, selectedFilters, user, isInitialLoad]);

  // Update the handler functions to use a single update method
  const updateTableState = (
    newParams?: Record<string, any>,
    newFilters?: Record<string, any>
  ) => {
    setIsInitialLoad(false);

    if (newParams) {
      setCurrentParams(prev => ({
        ...prev,
        ...newParams,
      }));
    }

    if (newFilters) {
      setSelectedFilters(prev => ({
        ...prev,
        ...newFilters,
      }));
    }
  };

  // Update the handlers to use the new updateTableState function
  const handleFilterChange = (filterKey: string, value: any) => {
    console.log('Filter Debug - handleFilterChange called:', {
      filterKey,
      value,
    });
    updateTableState(
      { page: 1 }, // Reset to first page
      { [filterKey]: value }
    );
  };

  const handleSortChange = (sortParams: Record<string, any>) => {
    setSelectedSort(sortParams);
    updateTableState({ page: 1 }, null); // Reset to first page when sorting
  };

  const handlePageChange = (page: number) => {
    updateTableState({ page }, null);
  };

  const handleSearch = (searchTerm: string) => {
    setSearch(searchTerm);
    debouncedSearch(searchTerm);
  };

  const handleReset = (): void => {
    setIsInitialLoad(false);
    setSearch(''); // Clear the search input
    setSelectedFilters({});
    setCurrentParams(block.default_query_params || {});
    navigate(location.pathname);
  };

  // Add new function to reset only filters
  const handleFilterReset = (): void => {
    setIsInitialLoad(false);
    setSearch(''); // Clear the search input
    setSelectedFilters({});
    setCurrentParams(block.default_query_params || {});
  };

  // Function to generate navigation URL for a row based on row_click_action
  const generateRowUrl = useCallback(
    (rowData: any, clickedColumn?: Column): string | null => {
      // Handle both single object and array formats for backward compatibility
      const rowClickActions = Array.isArray(block.row_click_action)
        ? block.row_click_action
        : block.row_click_action
          ? [block.row_click_action]
          : [];

      if (rowClickActions.length === 0) {
        return null;
      }

      // Find the appropriate action based on the clicked column
      let selectedAction = null;

      if (clickedColumn) {
        // Find action that matches the clicked column
        selectedAction = rowClickActions.find(
          action => action.enabled && action.column_key === clickedColumn.key
        );
      }

      // If no specific action found for the column, use the first enabled action
      if (!selectedAction) {
        selectedAction = rowClickActions.find(action => action.enabled);
      }

      if (!selectedAction || !selectedAction.action_api) {
        return null;
      }

      const { action_api, column_key } = selectedAction;

      // Extract the main ID value for navigation (this could be from any placeholder or column_key)
      let navigationId = null;

      // First try to get ID from curly brace placeholders
      const placeholderMatches = action_api.match(/\{([^}]+)\}/g);
      if (placeholderMatches) {
        // Use the first placeholder's value for navigation
        const firstPlaceholder = placeholderMatches[0].replace(/[{}]/g, '');
        navigationId = rowData[firstPlaceholder];
      }

      // Fallback to column_key if no placeholders found
      if (!navigationId && column_key) {
        navigationId = rowData[column_key];
      }

      if (!navigationId) {
        console.warn(`Row click action: No navigation ID found in row data`);
        return null;
      }

      // Determine navigation route based on action_api pattern
      if (action_api.includes('/deal_room/')) {
        return `/deal-room/${navigationId}?tab=opportunity_review`;
      } else if (action_api.includes('/response')) {
        // New behavior: load page JSON via backend page endpoint
        return `/response/${navigationId}`;
      } else if (
        action_api.includes('/channels') ||
        action_api.includes('/settings/channel')
      ) {
        // Channel details: navigate to new channels page
        return `/channels/${navigationId}`;
      } else if (action_api.includes('/conversation_details')) {
        return `/conversation/${navigationId}`;
      } else {
        // Generic fallback: extract the full resource path from the API URL
        const apiMatch = action_api.match(/\/v\d+\/(.+?)(?:\{[^}]+\}|$)/);
        if (apiMatch) {
          // Remove trailing slash if present and construct the route
          const resourcePath = apiMatch[1].replace(/\/$/, '');
          return `/${resourcePath}/${navigationId}`;
        } else {
          console.warn(
            `Row click action: Unable to determine navigation route for API: ${action_api}`
          );
          return null;
        }
      }
    },
    [block.row_click_action]
  );

  const handleRowClick = (rowData: any, clickedColumn?: Column) => {
    const url = generateRowUrl(rowData, clickedColumn);
    if (url) {
      navigate(url);
    }
  };

  const handleColumnVisibility = (): void => {
    setShowColumnVisibility(!showColumnVisibility);
  };

  const handleColumnToggle = (columnKey: string) => {
    setVisibleColumns(prev => {
      if (prev.includes(columnKey)) {
        // Don't allow hiding all columns
        if (prev.length === 1) return prev;
        return prev.filter(key => key !== columnKey);
      }
      return [...prev, columnKey];
    });
  };

  // Column resize handlers
  const handleColumnResize = useCallback(
    (columnKey: string, newWidth: number) => {
      setColumnWidths(prev => ({
        ...prev,
        [columnKey]: `${Math.max(50, newWidth)}px`, // Minimum width of 50px
      }));
    },
    []
  );

  // Ref to track active resize cleanup function
  const resizeCleanupRef = useRef<(() => void) | null>(null);

  const startColumnResize = useCallback(
    (e: React.MouseEvent, columnKey: string) => {
      e.preventDefault();

      // Clean up any existing resize operation
      if (resizeCleanupRef.current) {
        resizeCleanupRef.current();
        resizeCleanupRef.current = null;
      }

      const startX = e.clientX;

      // Safely parse width - handle numbers, strings with/without 'px'
      const currentWidth = columnWidths[columnKey];
      let startWidth;

      if (typeof currentWidth === 'number') {
        startWidth = currentWidth;
      } else if (typeof currentWidth === 'string') {
        // Remove 'px' if present and parse, fallback to 150 if invalid
        startWidth = parseInt(currentWidth.replace(/px$/, ''), 10) || 150;
      } else {
        startWidth = 150; // Default fallback
      }

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const deltaX = moveEvent.clientX - startX;
        const newWidth = startWidth + deltaX;
        handleColumnResize(columnKey, newWidth);
      };

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        resizeCleanupRef.current = null;
      };

      // Store cleanup function for potential early cleanup
      resizeCleanupRef.current = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [columnWidths, handleColumnResize]
  );

  // Cleanup effect for component unmount
  useEffect(() => {
    return () => {
      // Clean up any active resize operation when component unmounts
      if (resizeCleanupRef.current) {
        resizeCleanupRef.current();
        resizeCleanupRef.current = null;
      }
    };
  }, []);

  // Scroll handlers for arrow buttons
  const handleScrollLeft = () => {
    const container = tableContainerRef.current;
    if (container) {
      const scrollAmount = container.clientWidth * 0.8; // Scroll 80% of visible width
      container.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const handleScrollRight = () => {
    const container = tableContainerRef.current;
    if (container) {
      const scrollAmount = container.clientWidth * 0.8; // Scroll 80% of visible width
      container.scrollBy({
        left: scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  // Add scroll indicators to show when content is scrollable
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const updateScrollIndicators = () => {
      const hasHorizontalScroll = container.scrollWidth > container.clientWidth;
      const hasVerticalScroll = container.scrollHeight > container.clientHeight;

      // Check scroll position
      const isScrolledRight = container.scrollLeft > 0;
      const canScrollRight =
        container.scrollLeft <
        container.scrollWidth - container.clientWidth - 1;

      // Add/remove classes based on scroll state
      container.classList.toggle(
        'scrollable-right',
        hasHorizontalScroll && canScrollRight
      );
      container.classList.toggle(
        'scrollable-left',
        hasHorizontalScroll && isScrolledRight
      );

      // Update arrow visibility state
      setShowScrollLeft(hasHorizontalScroll && isScrolledRight);
      setShowScrollRight(hasHorizontalScroll && canScrollRight);
    };

    // Check on mount and when data/columns change
    updateScrollIndicators();

    // Listen to scroll events
    container.addEventListener('scroll', updateScrollIndicators, {
      passive: true,
    });

    // Use ResizeObserver to detect when content size changes
    const resizeObserver = new ResizeObserver(() => {
      updateScrollIndicators();
    });
    resizeObserver.observe(container);

    // Also observe the table itself for width changes
    const table = container.querySelector('table');
    if (table) {
      resizeObserver.observe(table);
    }

    return () => {
      container.removeEventListener('scroll', updateScrollIndicators);
      resizeObserver.disconnect();
    };
  }, [data, block.columns, columnWidths]);

  // const TableBody = ({ data, columns, loading, onRowClick }) => {
  //   return (
  //     <tbody>
  //       {loading ? (
  //         <tr>
  //           <td colSpan={columns.length} className="loading-cell">
  //             Loading...
  //           </td>
  //         </tr>
  //       ) : data.length === 0 ? (
  //         <tr>
  //           <td colSpan={columns.length} className="no-data-cell">
  //             No data available
  //           </td>
  //         </tr>
  //       ) : (
  //         data.map((row, index) => (
  //           <tr
  //             key={index}
  //             onClick={() => onRowClick && onRowClick(row)}
  //             className={block.row_click_action?.enabled ? 'clickable-row' : ''}
  //           >
  //             {columns.map((column) => (
  //               <td key={column.key}>
  //                 {row[column.key]}
  //               </td>
  //             ))}
  //           </tr>
  //         ))
  //       )}
  //     </tbody>
  //   );
  // };

  // Handle errors
  if (error) {
    console.error('Table data fetch error:', error);
  }

  return (
    <div
      ref={tableRef}
      id={block.id || `table-${Math.random().toString(36).substr(2, 9)}`}
      className="ept-data-table"
    >
      {block.tableTitle && <h2 className="table-title">{block.tableTitle}</h2>}

      {error && (
        <div
          className="error-message"
          style={{
            padding: '10px',
            margin: '10px 0',
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '4px',
            color: '#c33',
          }}
        >
          Error loading data:{' '}
          {error instanceof Error ? error.message : 'Unknown error'}
          <button
            onClick={() => refetchData()}
            style={{ marginLeft: '10px', padding: '2px 8px' }}
          >
            Retry
          </button>
        </div>
      )}

      <div className="table-controls">
        <div className="controls-left">
          {block.search?.enabled && (
            <div className="search-container">
              <input
                type="text"
                value={search}
                placeholder="Search..."
                onChange={e => handleSearch(e.target.value)}
                className="search-input"
              />
              {(block.filters?.length > 0 ||
                Object.keys(selectedFilters).length > 0 ||
                search) && (
                <button
                  onClick={handleFilterReset}
                  className="filter-reset-button"
                  title="Reset all filters"
                >
                  Clear Filters
                </button>
              )}
            </div>
          )}

          {block.table_features?.showResetButton && (
            <button onClick={handleReset} className="reset-button">
              Reset
            </button>
          )}
          {block.table_features?.showColumnVisibility && (
            <div className="column-visibility-container">
              <button
                onClick={handleColumnVisibility}
                className="column-visibility-button"
              ></button>
              {showColumnVisibility && (
                <ColumnVisibilityDropdown
                  columns={block.columns}
                  visibleColumns={visibleColumns}
                  onColumnToggle={handleColumnToggle}
                  onClose={() => setShowColumnVisibility(false)}
                />
              )}
            </div>
          )}
        </div>
      </div>

      <div className="table-wrapper" style={{ position: 'relative' }}>
        {/* Modern scroll arrow indicators - outside scrollable container */}
        {showScrollLeft && (
          <div
            className="scroll-indicator-arrow scroll-indicator-arrow-left show"
            onClick={handleScrollLeft}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleScrollLeft();
              }
            }}
            aria-label="Scroll left"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </div>
        )}
        {showScrollRight && (
          <div
            className="scroll-indicator-arrow scroll-indicator-arrow-right show"
            onClick={handleScrollRight}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleScrollRight();
              }
            }}
            aria-label="Scroll right"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
          </div>
        )}
        <div className="table-container" ref={tableContainerRef}>
          <table>
            <TableHeader
              columns={block.columns.filter(col =>
                visibleColumns.includes(col.key)
              )}
              sorting={block.sorting}
              filters={block.filters}
              onFilterChange={handleFilterChange}
              selectedFilters={selectedFilters}
              onSortChange={handleSortChange}
              selectedSort={selectedSort}
              columnWidths={columnWidths}
              columnMinWidths={columnMinWidths}
              startColumnResize={startColumnResize}
            />
            <TableBody
              data={data}
              columns={block.columns.filter(col =>
                visibleColumns.includes(col.key)
              )}
              loading={loading}
              onRowClick={handleRowClick}
              generateRowUrl={generateRowUrl}
              rowClickAction={block.row_click_action}
              onDataReload={fetchData}
              responseId={responseId}
              columnWidths={columnWidths}
              columnMinWidths={columnMinWidths}
              tableId={block.id}
            />
          </table>
        </div>
      </div>

      {block.table_features?.pagination?.enabled && (
        <TablePagination
          currentPage={currentParams.page}
          totalRows={totalRows}
          limit={block.table_features?.pagination?.default_limit}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};

export default EptDataTable;
